import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Users } from 'lucide-react';

const normalizeName = (value, fallback) => String(value || fallback).trim();

const TeamHierarchyPicker = ({ teams = [], value = '', onChange, allowUnassigned = true }) => {
  const [expandedSectors, setExpandedSectors] = useState({});
  const [expandedDepartments, setExpandedDepartments] = useState({});

  const hierarchy = useMemo(() => {
    const sectors = new Map();
    teams.forEach((team) => {
      const sectorName = normalizeName(team.sectorName, 'Registered teams');
      const departmentName = normalizeName(team.deptName, 'Teams');
      if (!sectors.has(sectorName)) sectors.set(sectorName, new Map());
      const departments = sectors.get(sectorName);
      if (!departments.has(departmentName)) departments.set(departmentName, []);
      departments.get(departmentName).push(team);
    });
    return [...sectors.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([sectorName, departments]) => ({
        name: sectorName,
        departments: [...departments.entries()]
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([departmentName, departmentTeams]) => ({
            name: departmentName,
            teams: [...departmentTeams].sort((left, right) => left.name.localeCompare(right.name)),
          })),
      }));
  }, [teams]);

  useEffect(() => {
    const selectedTeam = teams.find((team) => team.id === value);
    if (!selectedTeam) return;
    const sectorName = normalizeName(selectedTeam.sectorName, 'Registered teams');
    const departmentName = normalizeName(selectedTeam.deptName, 'Teams');
    setExpandedSectors((current) => ({ ...current, [sectorName]: true }));
    setExpandedDepartments((current) => ({ ...current, [`${sectorName}::${departmentName}`]: true }));
  }, [teams, value]);

  if (teams.length === 0) {
    return <p className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-500">No registered teams are available.</p>;
  }

  return (
    <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2" data-testid="team-hierarchy-picker">
      {allowUnassigned && (
        <button type="button" onClick={() => onChange?.('')} className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm ${!value ? 'bg-sky-50 text-sky-800' : 'hover:bg-slate-50'}`} data-testid="team-option-unassigned">
          <span className="flex h-5 w-5 items-center justify-center">{!value && <Check size={14} />}</span>
          Keep unassigned
        </button>
      )}
      {hierarchy.map((sector) => {
        const sectorExpanded = !!expandedSectors[sector.name];
        return (
          <div key={sector.name} className="rounded-md border border-slate-100">
            <button type="button" onClick={() => setExpandedSectors((current) => ({ ...current, [sector.name]: !sectorExpanded }))} className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm font-medium text-slate-800 hover:bg-slate-50" aria-expanded={sectorExpanded}>
              {sectorExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span className="min-w-0 flex-1 truncate">{sector.name}</span>
              <span className="text-[11px] font-normal text-slate-400">{sector.departments.reduce((total, department) => total + department.teams.length, 0)}</span>
            </button>
            {sectorExpanded && (
              <div className="border-t border-slate-100 p-1.5 pl-3">
                {sector.departments.map((department) => {
                  const departmentKey = `${sector.name}::${department.name}`;
                  const departmentExpanded = !!expandedDepartments[departmentKey];
                  return (
                    <div key={departmentKey}>
                      <button type="button" onClick={() => setExpandedDepartments((current) => ({ ...current, [departmentKey]: !departmentExpanded }))} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium text-slate-600 hover:bg-slate-50" aria-expanded={departmentExpanded}>
                        {departmentExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        <span className="min-w-0 flex-1 truncate">{department.name}</span>
                        <span className="text-[10px] font-normal text-slate-400">{department.teams.length}</span>
                      </button>
                      {departmentExpanded && (
                        <div className="ml-4 border-l border-slate-100 pl-2">
                          {department.teams.map((team) => {
                            const selected = team.id === value;
                            return (
                              <button key={team.id} type="button" onClick={() => onChange?.(team.id)} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${selected ? 'bg-sky-50 text-sky-800' : 'text-slate-700 hover:bg-slate-50'}`} data-testid={`team-option-${team.id}`}>
                                <Users size={13} className="shrink-0" />
                                <span className="min-w-0 flex-1 truncate">{team.name}</span>
                                {selected && <Check size={14} className="shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TeamHierarchyPicker;
